/**
 * Print Interceptor — Transparently intercepts Axios.post() calls to the
 * external hardware controller and reroutes them through Electron's embedded
 * print service via IPC.
 *
 * Key constraint: Does NOT modify any of the ~20 existing printer files.
 * Installs as a global Axios interceptor on app startup.
 */

import axios from "axios";
import { templateToHtml } from "./receiptHtmlRenderer";
import useAuthStore from "../../stores/useAuthStore";
import useCartStore from "../../stores/useCartStore";
import { notifyNoPrinterSelected } from "./printTemplateErrors";

let interceptorInstalled = false;

/**
 * Get the hardware controller URL from tillData in localStorage.
 * @returns {string|null}
 */
function getHardwareControllerUrl() {
  try {
    const tillData = useAuthStore.getState().tillData;
    return tillData?.tillAccess?.cwrTill?.hardwareController?.imageUrl || null;
  } catch {
    return null;
  }
}

/**
 * Check if embedded printing is enabled.
 * @returns {Promise<boolean>}
 */
async function isEmbeddedPrintEnabled() {
  if (!window.electronAPI) return false;
  try {
    const val = await window.electronAPI?.hardware?.getHwConfig("useEmbeddedPrint");
    return val !== "false";
  } catch {
    return true; // default enabled
  }
}

/**
 * Get the default printer name from Electron config.
 * @returns {Promise<string|null>}
 */
async function getDefaultPrinter() {
  try {
    return await window.electronAPI?.hardware?.getDefaultPrinter();
  } catch {
    return null;
  }
}

/**
 * Extract document number from receipt data in localStorage (reliable source).
 * Falls back to scanning template text elements.
 */
function extractDocumentNo(receiptJson) {
  // 1. Use embedded metadata from CWHardwareController (set at print time with correct order data)
  if (receiptJson?.documentNo) return receiptJson.documentNo;

  // 2. Try cartObj in Zustand store
  try {
    const cartObj = useCartStore.getState().cart;
    if (cartObj?.documentno) return cartObj.documentno;
  } catch {
    /* ignore */
  }

  // 3. Scan template text elements for document number patterns
  if (receiptJson?.elements) {
    for (const el of receiptJson.elements) {
      if (el.type === "text" && el.content) {
        const match = el.content.match(/(?:Doc|Invoice|Receipt|Order|Bill)\s*#?\s*[:.]\s*(\S+)/i);
        if (match) return match[1];
      }
    }
  }

  // 4. Fallback: generate a timestamp-based ID so we never get null
  return `RCP-${Date.now()}`;
}

/**
 * Detect receipt type from the receipt data context and template content.
 */
function detectReceiptType(receiptJson) {
  // 1. Use embedded metadata from CWHardwareController
  if (receiptJson?.receiptType) return receiptJson.receiptType;

  // 2. Try to detect from cartObj in Zustand store
  try {
    const cartObj = useCartStore.getState().cart;
    if (cartObj?.isReturn === "Y" || cartObj?.isReturn === true) return "return";
    if (cartObj?.layAway === "Y") return "layaway";
  } catch {
    /* ignore */
  }

  // 3. Scan template text
  if (receiptJson?.elements) {
    const text = receiptJson.elements
      .filter((el) => el.type === "text")
      .map((el) => el.content || "")
      .join(" ")
      .toLowerCase();

    if (text.includes("return") || text.includes("refund")) return "return";
    if (text.includes("kot") || text.includes("kitchen")) return "kot";
    if (text.includes("till close") || text.includes("end of day")) return "till-close";
    if (text.includes("gift card") || text.includes("credit note")) return "gift-card";
    if (text.includes("estimate") || text.includes("quotation")) return "estimate";
    if (text.includes("label")) return "label";
  }

  return "sale";
}

/**
 * Archive a receipt as PDF. Always runs regardless of physical printer status.
 */
async function archiveReceiptPdf(htmlContent, receiptJson) {
  const documentNo = extractDocumentNo(receiptJson);
  const receiptType = detectReceiptType(receiptJson);

  // receiptJson is the rendered print template (only ever has an `elements`
  // array — CWHardwareController's processReceiptTemplate() strips everything
  // else before sending). The real transaction numbers live in the live cart
  // state instead, which is why these are read from useCartStore, not receiptJson.
  let cartObj = {};
  try {
    cartObj = useCartStore.getState().cart || {};
  } catch {
    /* ignore */
  }

  let storeId = null;
  let terminalId = null;
  try {
    const tillData = useAuthStore.getState().tillData;
    storeId = tillData?.tillAccess?.csBunit?.csBunitId || null;
    terminalId = tillData?.tillAccess?.cwrTill?.cwrTillID || null;
  } catch {
    /* ignore */
  }

  const taxRateGroups = Array.isArray(cartObj.groupedItemsByTaxRate) ? cartObj.groupedItemsByTaxRate : [];
  const sumGroupField = (field) => taxRateGroups.reduce((sum, group) => sum + (group?.[field] || 0), 0);

  const discountAmount = cartObj.discount ?? null;
  const totalQty = cartObj.totalQty ?? null;
  const netAmount = taxRateGroups.length ? sumGroupField("totalLinenet") : null;
  const grossAmount = taxRateGroups.length ? sumGroupField("totalLinegross") : null;
  const taxAmount = taxRateGroups.length ? sumGroupField("totalLinetax") : null;
  const paymentSummary = Array.isArray(cartObj.payments) && cartObj.payments.length ? cartObj.payments.map((p) => ({ name: p?.name || null, amount: p?.amount ?? null })) : null;

  try {
    const result = await window.electronAPI.hardware.printToPdf(htmlContent, {
      documentNo,
      receiptType,
      metadata: {
        storeId,
        terminalId,
        totalQty,
        grossAmount,
        discountAmount,
        taxAmount,
        netAmount,
        paymentSummary,
      },
    });
    if (result.pdfPath) {
    } else {
      console.warn("[PrintInterceptor] PDF archive returned no path:", result.error);
    }
    return result;
  } catch (err) {
    console.error("[PrintInterceptor] PDF archive failed:", err);
    return { pdfPath: null, error: err.message };
  }
}

/**
 * Install the Axios interceptor to reroute print calls through Electron IPC.
 * Should be called once on app startup when running in Electron.
 */
export function installPrintInterceptor() {
  console.debug("[printInterceptor] installPrintInterceptor called. already installed:", interceptorInstalled, "electronAPI present:", !!window.electronAPI);
  if (interceptorInstalled) return;
  if (!window.electronAPI) return;

  interceptorInstalled = true;
  console.debug("[printInterceptor] interceptor attached to axios");

  axios.interceptors.request.use(
    async (config) => {
      console.debug("[printInterceptor] axios request seen:", config.method, config.url);
      // Only intercept POST requests
      if (config.method?.toLowerCase() !== "post") return config;

      const controllerUrl = getHardwareControllerUrl();
      if (!controllerUrl) return config;

      // Check if this URL targets the hardware controller printer endpoint
      const isPrinterRequest = config.url && config.url.includes(controllerUrl) && config.url.includes("printer");
      if (config.url && config.url.includes("printer")) {
        console.debug("[printInterceptor] printer-ish POST seen:", { url: config.url, controllerUrl, isPrinterRequest, isMobile: window.electronAPI?.isMobile });
      }
      if (!isPrinterRequest) return config;

      // Check if embedded printing is enabled
      const embedded = await isEmbeddedPrintEnabled();
      console.debug("[printInterceptor] embedded print enabled:", embedded);
      if (!embedded) return config;

      try {
        // Parse the receipt data from the request body
        let receiptJson;
        if (typeof config.data === "string") {
          try {
            receiptJson = JSON.parse(config.data);
          } catch (e) {
            // Not JSON — might be XML or plain text, let it through
            console.debug("[printInterceptor] payload is not JSON, letting original request through:", e.message);
            return config;
          }
        } else {
          receiptJson = config.data;
        }

        // Check if this is a cash drawer open command
        if (receiptJson?.cash_drawer?.action === "open") {
          // Use openDrawerConfigured so the actual ESC/POS kick command is sent,
          // not just an audit log entry (logDrawerEvent was audit-only).
          window.electronAPI.hardware.openDrawerConfigured("sale").catch((err) => console.warn("[printInterceptor] openDrawerConfigured failed:", err?.message));
          // Cancel the original request — drawer handled natively
          const source = axios.CancelToken.source();
          config.cancelToken = source.token;
          source.cancel("Handled by embedded drawer service");
          return config;
        }

        // Check if this looks like a processed template (has elements array)
        if (!receiptJson?.elements || !Array.isArray(receiptJson.elements)) {
          // Not a receipt template — let it through to external controller
          console.debug("[printInterceptor] payload has no elements array, letting original request through:", Object.keys(receiptJson || {}));
          return config;
        }

        // --- Resolve target printer ---
        // KOT payloads include a `printer` field for multi-printer routing
        const kotPrinterId = receiptJson.printer || null;
        let printerName = kotPrinterId || (await getDefaultPrinter());
        console.debug("[printInterceptor] resolved printerName:", printerName, "from kotPrinterId:", kotPrinterId);

        const htmlContent = templateToHtml(receiptJson);

        if (printerName) {
          // Send raw JSON to browser — browser-side receiptRenderer handles full layout
          // (startPosition, subheaders, barcodes, extraRows, etc.)
          let result;
          if (window.electronAPI.hardware.printTemplate) {
            result = await window.electronAPI.hardware.printTemplate(printerName, receiptJson, { silent: true });
          } else {
            result = await window.electronAPI.hardware.printDirect(printerName, htmlContent, { silent: true });
          }

          if (!result.success) {
            console.warn("[PrintInterceptor] Embedded print failed:", result.error);
            // printTemplate() only archives internally on success — archive here so a
            // failed print still leaves a record, matching the original fallback intent
            archiveReceiptPdf(htmlContent, receiptJson);
            return config;
          }

          // printDirect (used when printTemplate isn't available) has no internal archive step
          if (!window.electronAPI.hardware.printTemplate) {
            archiveReceiptPdf(htmlContent, receiptJson);
          }
        } else {
          // No printer configured — printTemplate never runs, so nothing else will archive this
          archiveReceiptPdf(htmlContent, receiptJson);
          notifyNoPrinterSelected();
        }

        // Cancel the original Axios request (handled by embedded service)
        const cancelSource = axios.CancelToken.source();
        config.cancelToken = cancelSource.token;
        cancelSource.cancel("Handled by embedded printer");
        return config;
      } catch (err) {
        // Fallback: let original Axios request proceed to external controller
        console.warn("[PrintInterceptor] Embedded print failed, falling back:", err.message || err);
        return config;
      }
    },
    (error) => Promise.reject(error)
  );

  // Also add a response interceptor to silently handle cancelled requests
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isCancel(error) && error.message?.includes("embedded")) {
        // Return a fake success response for cancelled printer requests
        return Promise.resolve({ data: { success: true, source: "embedded" }, status: 200 });
      }
      return Promise.reject(error);
    }
  );
}

export default installPrintInterceptor;
